import { Menu } from 'lucide-react';
import { FamilySwitcher } from '@/features/family/components/FamilySwitcher';
import { useRealtimeNotifications } from '@/features/notifications/hooks/useRealtimeNotifications';
import { queryRoom } from '@/features/p2p-chat';
import { PendingSyncBadge } from '@/shared/components/feedback/PendingSyncBadge';
import {
  OfflineIndicator,
  UpdateAvailableBadge,
} from '@/shared/components/feedback/ServiceWorkerUpdate';
import {
  BrandLogo,
  BrandLogoSize,
  NotificationBell,
} from '@/shared/components/ui';
import { Button } from '@/shared/components/ui/button';
import { useOnlineStatus } from '@/shared/hooks/useOnlineStatus';
import { ButtonVariant, ButtonSize } from '@/shared/types/button.types';
import { UserMenu } from './UserMenu';

interface HeaderProps {
  readonly onMenuClick: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const isOnline = useOnlineStatus();

  // P7: live notifications (no-op when realtime is disabled).
  useRealtimeNotifications();

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-card">
      <div className="flex h-16 items-center px-3 sm:px-6">
        <Button
          variant={ButtonVariant.Ghost}
          size={ButtonSize.Icon}
          className="mr-2 sm:mr-4 lg:hidden"
          onClick={onMenuClick}
          aria-label="Toggle menu"
        >
          <Menu className="size-5" />
        </Button>

        <BrandLogo size={BrandLogoSize.Small} showText />

        <div className="ml-2 hidden sm:ml-4 sm:block">
          <FamilySwitcher />
        </div>

        <div className="ml-auto flex items-center gap-2 sm:gap-4">
          <OfflineIndicator isOnline={isOnline} />
          <PendingSyncBadge />
          <UpdateAvailableBadge />
          <NotificationBell
            onValidateChatRoom={async (roomCode) =>
              (await queryRoom(roomCode)).exists
            }
          />
          <UserMenu />
        </div>
      </div>
    </header>
  );
}
